import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const OEMLanding = () => {
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: '/oem',
      audience: 'OEM'
    });
  }, []);

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>URGAA for OEMs - White-Label Charging Network Platform | Go4Garage</title>
        <meta name="title" content="URGAA for OEMs - White-Label Charging Network Platform" />
        <meta name="description" content="Launch your branded charging network in 30 days. White-label URGAA platform with full customization. Perfect for EV OEMs offering integrated charging to customers. Used by 5+ OEMs." />
        <meta name="keywords" content="OEM charging network, white label charging platform, EV OEM software, branded charging network, EV manufacturer charging solution, automotive charging platform India" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urgaa.in/oem" />
        <meta property="og:title" content="URGAA for OEMs - White-Label Charging Network Platform" />
        <meta property="og:description" content="Launch your branded charging network in 30 days with white-label URGAA platform." />
        <meta property="og:image" content="https://urgaa.in/images/og-oem.jpg" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://urgaa.in/oem" />
        <meta name="twitter:title" content="URGAA for OEMs" />
        <meta name="twitter:description" content="White-label charging network platform for EV manufacturers" />
        <meta name="twitter:image" content="https://urgaa.in/images/twitter-oem.jpg" />
        <meta name="twitter:site" content="@Go4Garage" />
        
        {/* Additional Meta */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urgaa.in/oem" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "URGAA for OEMs",
            "applicationCategory": "BusinessApplication",
            "description": "White-label charging network platform for EV OEMs",
            "provider": {
              "@type": "Organization",
              "name": "Go4Garage Private Limited",
              "url": "https://urgaa.in"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold mb-6">
              🚗 5+ OEMs | 500+ Branded Charging Stations
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Launch Your Branded<br />Charging Network in 30 Days
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              White-label URGAA platform. Your brand, your customers, your charging network. Full control without the tech complexity.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/login" className="px-8 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold">
                Request Demo
              </Link>
              <a href="#features" className="px-8 py-4 border-2 border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 font-semibold">
                Learn More
              </a>
            </div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-16 bg-red-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Why OEMs Struggle with Charging Networks</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">⏱️</div>
                <h3 className="text-xl font-bold mb-2">18-24 Month Build Time</h3>
                <p className="text-gray-600">Building charging platform in-house takes 2 years and ₹15+ crore capex.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">🔌</div>
                <h3 className="text-xl font-bold mb-2">Vendor Lock-In</h3>
                <p className="text-gray-600">Existing platforms tie you to their hardware. Can't onboard third-party chargers.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">👥</div>
                <h3 className="text-xl font-bold mb-2">Customer Complaints</h3>
                <p className="text-gray-600">"Charger was down again." Poor uptime hurts brand reputation and NPS.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section id="features" className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">URGAA White-Label Solution</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border-2 border-indigo-500 p-6 rounded-lg">
                <div className="text-indigo-600 text-4xl mb-4">🎨</div>
                <h3 className="text-xl font-bold mb-2">Fully Branded Platform</h3>
                <p className="text-gray-600 mb-4">Your logo, colors, domain. Customers see only your brand, not ours.</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Custom domain (e.g., charging.yourbrand.com)</li>
                  <li>✓ White-label mobile app (iOS & Android)</li>
                  <li>✓ Branded customer emails & notifications</li>
                  <li>✓ Custom pricing & subscription models</li>
                </ul>
              </div>
              <div className="border-2 border-blue-500 p-6 rounded-lg">
                <div className="text-blue-600 text-4xl mb-4">⚡</div>
                <h3 className="text-xl font-bold mb-2">30-Day Launch Timeline</h3>
                <p className="text-gray-600 mb-4">Go live in 1 month vs 18-24 months in-house build</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Week 1: Brand customization & setup</li>
                  <li>✓ Week 2-3: Charger integration (OCPP 1.6/2.0)</li>
                  <li>✓ Week 4: Testing & launch</li>
                  <li>✓ Save ₹15+ crore in development costs</li>
                </ul>
              </div>
              <div className="border-2 border-green-500 p-6 rounded-lg">
                <div className="text-green-600 text-4xl mb-4">🔌</div>
                <h3 className="text-xl font-bold mb-2">Hardware Agnostic</h3>
                <p className="text-gray-600 mb-4">Works with any OCPP charger. No vendor lock-in.</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ OCPP 1.6 & 2.0 support</li>
                  <li>✓ Works with ABB, Delta, Exicom, etc.</li>
                  <li>✓ Onboard existing third-party networks</li>
                  <li>✓ Future-proof for new charger models</li>
                </ul>
              </div>
              <div className="border-2 border-purple-500 p-6 rounded-lg">
                <div className="text-purple-600 text-4xl mb-4">📊</div>
                <h3 className="text-xl font-bold mb-2">Complete Control Panel</h3>
                <p className="text-gray-600 mb-4">Manage your entire network from one dashboard</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Real-time NOC monitoring</li>
                  <li>✓ Customer management & support</li>
                  <li>✓ Revenue & usage analytics</li>
                  <li>✓ SLA tracking & automated credits</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Seamless Integration with Your Ecosystem</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-3">🚗 Vehicle Telematics</h3>
                <p className="text-sm text-gray-600">Sync battery SoC, location, and range data from your vehicles</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-3">📱 Mobile App SDK</h3>
                <p className="text-sm text-gray-600">Embed charging features directly in your existing OEM app</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-3">💳 Payment Gateway</h3>
                <p className="text-sm text-gray-600">Use your existing payment provider or wallet system</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">OEM Partnership Pricing</h2>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-5xl font-bold text-indigo-600 mb-2">Custom</div>
              <div className="text-gray-600 mb-6">Based on your network size and features</div>
              <ul className="text-left space-y-3 mb-8 max-w-md mx-auto">
                <li>✓ One-time setup fee (₹15-25 lakhs)</li>
                <li>✓ Per-station monthly fee (₹1,500-2,500)</li>
                <li>✓ Revenue sharing options available</li>
                <li>✓ Volume discounts for 100+ stations</li>
                <li>✓ White-label mobile app included</li>
                <li>✓ Dedicated technical support team</li>
              </ul>
              <Link to="/login" className="w-full block py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold">
                Request Partnership Proposal
              </Link>
              <p className="text-sm text-gray-500 mt-4">Pilot projects available | Reference customer intros upon request</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-indigo-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Launch Your Charging Network?</h2>
            <p className="text-xl mb-8">Join leading OEMs offering integrated charging to their customers</p>
            <Link to="/login" className="px-8 py-4 bg-white text-indigo-600 rounded-lg hover:bg-gray-100 font-semibold inline-block">
              Schedule Technical Demo →
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default OEMLanding;